import { useState } from 'react';
import './roster-page.styles.scss';
import { Link } from 'react-router-dom';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import { formatMoney } from '@shared/contracts.js';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import { useSortable } from '@/utils/useSortable';
import TeamNav from '@/components/HOME/team-nav/team-nav.component';

// Visible ratings only: managers never see hidden traits.
const RATINGS = [
	{ key: 'ovr', label: 'Ovr', get: p => p.overall, strong: true },
	{ key: 'off', label: 'Off', get: p => p.off_quality },
	{ key: 'def', label: 'Def', get: p => p.def_quality },
	{ key: 'sta', label: 'Sta', get: p => p.stamina },
	{ key: '3pt', label: '3PT', get: p => p.off_3pt, title: '3-point shooting' },
	{ key: 'mid', label: 'Mid', get: p => p.off_shooting, title: 'Mid-range shooting' },
	{ key: 'drv', label: 'Drive', get: p => p.off_drive },
	{ key: 'pas', label: 'Pass', get: p => p.off_passing },
	{ key: 'oreb', label: 'OReb', get: p => p.off_rebounds, title: 'Offensive rebounding' },
	{ key: 'oiq', label: 'OIQ', get: p => p.off_smarts, title: 'Offensive IQ' },
	{ key: 'per', label: 'PerD', get: p => p.def_speed, title: 'Perimeter defense' },
	{ key: 'int', label: 'IntD', get: p => p.def_paint, title: 'Interior defense' },
	{ key: 'stl', label: 'Stl', get: p => p.def_steals },
	{ key: 'blk', label: 'Blk', get: p => p.def_blocks },
	{ key: 'dreb', label: 'DReb', get: p => p.def_rebounds, title: 'Defensive rebounding' },
	{ key: 'diq', label: 'DIQ', get: p => p.def_smarts, title: 'Defensive IQ' },
];

// Box score columns. made/att pairs show as "4.2-9.8" with their own
// percentage column. low: lower is better (sorts ascending first).
const STATS = [
	{ key: 'min', label: 'MIN' },
	{ key: 'pts', label: 'PTS' },
	{ key: 'fg', label: 'FG', made: 'fgm', att: 'fga' },
	{ key: 'fgp', label: 'FG%', pct: ['fgm', 'fga'] },
	{ key: 'tp', label: '3P', made: 'tpm', att: 'tpa' },
	{ key: 'tpp', label: '3P%', pct: ['tpm', 'tpa'] },
	{ key: 'ft', label: 'FT', made: 'ftm', att: 'fta' },
	{ key: 'ftp', label: 'FT%', pct: ['ftm', 'fta'] },
	{ key: 'oreb', label: 'OREB' },
	{ key: 'dreb', label: 'DREB' },
	{ key: 'reb', label: 'REB', sum: ['oreb', 'dreb'] },
	{ key: 'ast', label: 'AST' },
	{ key: 'stl', label: 'STL' },
	{ key: 'blk', label: 'BLK' },
	{ key: 'tov', label: 'TOV', low: true },
	{ key: 'pf', label: 'PF', low: true },
];

// The manager's full roster: every visible rating, and season (or pro
// playoff) stats per game or in total, with the team's totals underneath.
const RosterPage = ({ league, team, base, playerLink }) => {

	const [view, setView] = useState('avg');
	const sorter = useSortable();

	const list = Object.values(team.players);
	const hasPlayoffs = list.some(p => p.playoffs?.gp > 0);
	const playoffs = view === 'post';
	const statsOf = (p) => (playoffs ? p.playoffs : p.season) ?? {};
	const teamTotals = (playoffs ? team.playoffTeamStats : team.teamStats) ?? null;
	const perGame = view !== 'totals';
	const contracts = list.some(p => p.contract);

	// A stat's raw number for one stat line (null when there is none).
	const value = (s, c) => {
		if (c.pct) return s[c.pct[1]] ? s[c.pct[0]] / s[c.pct[1]] : null;
		const raw = c.sum ? c.sum.reduce((t, k) => t + (s[k] ?? 0), 0) : s[c.made ?? c.key];
		if (raw === undefined || !s.gp) return null;
		return perGame ? raw / s.gp : raw;
	};
	const cell = (s, c) => {
		if (c.pct) return s[c.pct[1]] ? `${(s[c.pct[0]] / s[c.pct[1]] * 100).toFixed(1)}` : '-';
		if (!s.gp || (c.key === 'min' && s.min === undefined)) return '-';
		const fmt = (x) => (perGame ? (x / s.gp).toFixed(1) : x);
		if (c.made) return `${fmt(s[c.made])}-${fmt(s[c.att])}`;
		return fmt(c.sum ? c.sum.reduce((t, k) => t + (s[k] ?? 0), 0) : s[c.key]);
	};

	const players = sorter.apply(list.sort((a, b) => a.pos1 - b.pos1 || b.overall - a.overall), {
		name: p => p.lName, pos: p => p.pos1, klass: p => p.year ?? p.age, ht: p => p.height,
		salary: p => p.contract?.salary ?? null, years: p => p.contract?.years ?? null,
		gp: p => statsOf(p).gp ?? 0, gs: p => statsOf(p).gs ?? 0,
		...Object.fromEntries(RATINGS.map(r => [r.key, r.get])),
		...Object.fromEntries(STATS.map(c => [`s_${c.key}`, p => value(statsOf(p), c)])),
	});
	const th = (k, label, first, className = 'num', title) =>
		<SortTh key={k} k={k} label={label} sorter={sorter} first={first} className={className} title={title} />;
	const starters = new Set(positions.map(p => team.depth?.[`${p.id}_0`]));

	const views = [
		{ key: 'ratings', label: 'Ratings' },
		{ key: 'avg', label: 'Per game' },
		{ key: 'totals', label: 'Totals' },
		...(hasPlayoffs ? [{ key: 'post', label: 'Playoffs' }] : []),
	];
	const ratings = view === 'ratings';

	return (
		<div className='roster-page'>
			<TeamNav base={base} />
			<div className='g-card'>
				<h2>{team.name} roster</h2>
				<p className='meta'>
					Starters are in bold. Tap a column to sort by it.
					{' '}<Link to={`${base}/lineup`}>Set your lineup</Link> · <Link to={`${base}/strategy`}>Choose a strategy</Link>
				</p>
				<div className='chips' role='tablist' aria-label='Roster view'>
					{views.map(v => (
						<button key={v.key} role='tab' aria-selected={v.key === view} className={`chip ${v.key === view ? 'active' : ''}`} onClick={() => setView(v.key)}>{v.label}</button>
					))}
				</div>
				<div className='table-scroll'>
					<table className='g-table roster-full'>
						<thead>
							<tr>
								{th('name', 'Player', 'asc', '')}
								{th('pos', 'Pos', 'asc', '')}
								{ratings ? <>
									{th('klass', list[0]?.year ? 'Yr' : 'Age', 'desc', '')}
									{th('ht', 'Ht')}
									{RATINGS.map(r => th(r.key, r.label, 'desc', 'num', r.title))}
									{contracts && th('salary', 'Salary')}
									{contracts && th('years', 'Yrs', 'desc', 'num', 'Years left on the contract, this season included')}
								</> : <>
									{th('gp', 'GP')}
									{th('gs', 'GS', 'desc', 'num', 'Games started')}
									{STATS.map(c => th(`s_${c.key}`, c.label, c.low ? 'asc' : 'desc'))}
								</>}
							</tr>
						</thead>
						<tbody>
							{players.map(p => {
								const s = statsOf(p);
								return (
									<tr key={p.id} className={p.injury?.gamesOut > 0 ? 'injured' : ''}>
										<td>
											<Link to={playerLink(team.id, p.id)} className={starters.has(p.id) ? 'starter' : ''}>{p.fName} {p.lName}</Link>
											{p.injury?.gamesOut > 0 && <span className='injury-tag'> Out {p.injury.gamesOut}</span>}
										</td>
										<td>{posLabel(p)}</td>
										{ratings ? <>
											<td>{classLabel(p)}</td>
											<td className='num'>{height(p.height)}</td>
											{RATINGS.map(r => <td key={r.key} className={`num ${r.strong ? 'strong' : ''}`}>{r.get(p) ?? '-'}</td>)}
											{contracts && <td className='num'>{p.contract ? formatMoney(p.contract.salary) : '-'}</td>}
											{contracts && <td className='num'>{p.contract ? (p.contract.years || 'Exp') : '-'}</td>}
										</> : <>
											<td className='num'>{s.gp ?? 0}</td>
											<td className='num'>{s.gs ?? 0}</td>
											{STATS.map(c => <td key={c.key} className='num'>{cell(s, c)}</td>)}
										</>}
									</tr>
								)
							})}
						</tbody>
						{!ratings && teamTotals?.gp > 0 &&
							<tfoot>
								<tr className='totals'>
									<td>Team</td>
									<td></td>
									<td className='num'>{teamTotals.gp}</td>
									<td></td>
									{/* Team totals have no minutes. */}
									{STATS.map(c => <td key={c.key} className='num'>{c.key === 'min' ? '' : cell(teamTotals, c)}</td>)}
								</tr>
							</tfoot>
						}
					</table>
				</div>
				{!ratings && <p className='meta'>{playoffs ? 'Playoff games only.' : perGame ? 'Per-game averages.' : 'Season totals.'} Percentages are made shots out of attempts.</p>}
			</div>
		</div>
	)
}

function posLabel(p) {
	return positions[p.pos1 - 1].short + (p.pos2 ? `/${positions[p.pos2 - 1].short}` : '');
}

function classLabel(p) {
	return p.year ? `${levels[p.year - 1].short}${p.redshirt ? ' (RS)' : p.fifthYear ? ' (5th)' : ''}` : p.age;
}

function height(inches) {
	return `${Math.floor(inches / 12)}'${inches % 12}"`;
}

export default RosterPage
